/**
 * Custom scripts for product pages only.
 * Loaded via snippets/scripts.liquid.
 */

import { CartLinesUpdateEvent } from "@shopify/events";

// Toggles the expanded state of the size banner in snippets/lc-product-gallery-tags.liquid
// used to show and hide the size of the model wearing the product.
document.addEventListener("click", (event) => {
  const sizeBanner = event.target.closest("[data-lc-size-banner-toggle]");
  if (sizeBanner) sizeBanner.classList.toggle("size-banner--active");
});

// Opens and refreshes the cart drawer after a third-party integration Rebold
// Bundle & Upsell adds to cart via its own AJAX call, bypassing our cart-lines-update event.
// `addedItems` is the `items` array from Rebold's own /cart/add.json response,
// passed straight through from reboltCartCallBack(response).
window.lcOpenCartDrawer = async function (addedItems) {
  // #cart-drawer only exists when settings.cart_type == 'drawer'.
  const cartDrawer = document.getElementById("cart-drawer");

  if (!cartDrawer) {
    window.location.href = Theme?.routes?.cart_url || "/cart";
    return;
  }

  const lines = (addedItems ?? []).map((item) => ({
    merchandiseId: String(item.variant_id ?? item.id),
    quantity: item.quantity,
  }));

  if (lines.length === 0) return;

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: "add",
      context: "product",
      lines,
      promise: deferredEventPromise.promise,
    }),
  );

  try {
    const response = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    const cart = await response.json();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: "rebold-bundle-upsell",
        didError: false,
      },
    });
  } catch (error) {
    deferredEventPromise.reject(error);
  }

  await customElements.whenDefined("theme-drawer");
  cartDrawer.open();
};

/** @param {number} amount */
function lcRoundMoney(amount) {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

// Product JSON without locale prefix returns the shop's primary language,
// which serves as the untranslated title source for tracking (prices are ignored: default market).
const lcDefaultLocaleProducts = new Map();
/** @param {string} handle */
function lcGetDefaultLocaleProduct(handle) {
  if (!lcDefaultLocaleProducts.has(handle)) {
    const request = fetch(`/products/${handle}.js`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        // Drop failed requests from the cache so a later event can retry.
        lcDefaultLocaleProducts.delete(handle);
        throw error;
      });
    lcDefaultLocaleProducts.set(handle, request);
  }
  return lcDefaultLocaleProducts.get(handle);
}

/**
 * Resolves one bundle component into a GA4-like item with untranslated names.
 * Falls back to the component's localized title so one failed lookup doesn't drop the event.
 * @param {{ handle?: string, productId?: number | string, variantId: number | string,
 *           fallbackTitle?: string, quantity?: number, price?: number }} component
 */
async function lcGetComponentDetail({ handle, productId, variantId, fallbackTitle, quantity, uvpPrice, price }) {
  let product;
  if (handle) {
    try {
      product = await lcGetDefaultLocaleProduct(handle);
    } catch {
      product = undefined;
    }
  }
  const variant = product?.variants?.find((v) => String(v.id) === String(variantId));
  return {
    item_id: String(variantId),
    item_group_id: productId != null ? String(productId) : product?.id != null ? String(product.id) : undefined,
    item_name: product?.title,
    item_variant: variant?.title ?? fallbackTitle,
    // Same price fields as mapItems() in the web pixel: list price, discount and paid price per unit
    uvp_price: uvpPrice ?? price,
    discount: lcRoundMoney((uvpPrice ?? price) - price),
    price,
    quantity: quantity ?? 1,
  };
}

/**
 * Builds the bundle item (for the GA4 items array) and the bundle object (contents).
 * @param {any[]} components
 * @param {number} value
 */
async function lcBuildBundlePayload(components, value) {
  // Rebold's own bundle handle also works outside product pages; the URL is the fallback.
  const bundleHandle =
    lcBundleHandle ?? window.location.pathname.split("/products/")[1]?.split("/")[0];
  const [bundle, componentDetails] = await Promise.all([
    lcGetDefaultLocaleProduct(bundleHandle),
    Promise.all(components.map(lcGetComponentDetail)),
  ]);

  const bundleVariantId = String(bundle.variants?.[0]?.id ?? bundle.id);
  const variantTitles = componentDetails
    .map((c) => c.item_variant)
    .filter(Boolean)
    .join(", ");

  // Bundle list price = sum of the components' list prices
  const uvpValue = lcRoundMoney(
    componentDetails.reduce((sum, c) => sum + (c.uvp_price ?? 0) * (c.quantity ?? 1), 0),
  );
  const discountValue = lcRoundMoney(Math.max(0, uvpValue - value));

  const item = {
    item_id: bundleVariantId, // variant ID, same as before the theme switch → matches feed
    item_group_id: String(bundle.id),
    item_name: bundle.title,
    item_brand: bundle.vendor,
    item_category: bundle.type,
    item_variant: variantTitles,
    uvp_price: uvpValue,
    discount: discountValue,
    price: value,
    quantity: 1,
  };

  const bundleObject = {
    bundle_id: String(bundle.id),
    bundle_variant_id: bundleVariantId,
    bundle_name: bundle.title,
    bundle_brand: bundle.vendor,
    bundle_category: bundle.type,
    bundle_variant: variantTitles,
    bundle_uvp_value: uvpValue,
    bundle_discount: discountValue,
    bundle_value: value,
    components: componentDetails,
  };

  return { item, bundle: bundleObject };
}

/**
 * @param {string} eventName
 * @param {any[]} components
 * @param {number} value
 * @returns {Promise<any|null>} the bundle object, or null if the bundle product couldn't be loaded
 */
async function lcPublishBundleEvent(eventName, components, value) {
  try {
    const { item, bundle } = await lcBuildBundlePayload(components, value);

    window.Shopify.analytics.publish(eventName, {
      currency: window.Shopify?.currency?.active,
      value,
      items: [item],
      bundles: [bundle],
    });
    return bundle;
  } catch {
    // Product fetch failed; skip rather than publish an incomplete event.
    return null;
  }
}

// Bundle mapping stored as cart attribute, so the web pixel can regroup
// the component lines into the bundle again from checkout onwards (checkout.attributes).
const LC_BUNDLE_ATTR = "_lc_bundles";

/** @param {any} bundle */
async function lcStoreBundleInCart(bundle) {
  try {
    const cartResponse = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    const cart = await cartResponse.json();

    let stored = [];
    try {
      stored = JSON.parse(cart.attributes?.[LC_BUNDLE_ATTR] || "[]");
      if (!Array.isArray(stored)) stored = [];
    } catch {
      stored = [];
    }

    // Drop bundles whose components are no longer in the cart.
    const inCart = new Set((cart.items ?? []).map((i) => String(i.variant_id)));
    stored = stored.filter((b) => (b.components ?? []).every((c) => inCart.has(String(c.item_id))));

    // One entry = one bundle unit. Prices are NOT stored; the pixel takes them from checkout.
    stored.push({
      bundle_id: bundle.bundle_id,
      bundle_variant_id: bundle.bundle_variant_id,
      bundle_name: bundle.bundle_name,
      bundle_brand: bundle.bundle_brand,
      bundle_category: bundle.bundle_category,
      bundle_variant: bundle.bundle_variant,
      components: bundle.components.map((c) => ({
        item_id: c.item_id,
        item_group_id: c.item_group_id,
        item_name: c.item_name,
        item_variant: c.item_variant,
        quantity: c.quantity,
      })),
    });

    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { [LC_BUNDLE_ATTR]: JSON.stringify(stored) } }),
    });
  } catch {
    // Tracking only; never break the cart flow.
  }
}

// Rebold's cart response has no product handles, so they are collected from its selection events.
const lcComponentHandles = new Map();
let lcBundleHandle;

// Rebold's "Callback" option lets us enable calling this global.
/** @param {any} response */
async function lcHandleReboltCartCallback(response) {
  /** @type {any[]} */
  const addedItems = response?.items ?? [];

  // Tell the pixel immediately (before any await) which lines belong to this bundle,
  // so it can drop Shopify's standard add_to_cart events for exactly these lines.
  if (addedItems.length > 0) {
    window.Shopify?.analytics?.publish("custom_bundle_components_added", {
      components: addedItems.map((item) => ({
        variant_id: String(item.variant_id ?? item.id),
        quantity: Number(item.quantity) || 1,
      })),
    });
  }

  // update and open cart after adding a bundle product to cart
  window.lcOpenCartDrawer?.(addedItems);

  if (addedItems.length === 0) return;

  // Rebold's own cart response has decimal-string prices (not Shopify's cent integers), and
  // `discounted_price` already reflects the bundle discount, unlike the plain `price` field.
  const value = lcRoundMoney(
    addedItems.reduce(
      (sum, item) =>
        sum +
        parseFloat(item.discounted_price ?? item.price ?? 0) * item.quantity,
      0,
    ),
  );

  const bundle = await lcPublishBundleEvent(
    "custom_bundle_add_to_cart_event",
    addedItems.map((item) => ({
      handle: lcComponentHandles.get(String(item.product_id)),
      productId: item.product_id,
      variantId: item.variant_id,
      fallbackTitle: item.variant_title,
      quantity: Number(item.quantity) || 1,
      uvpPrice: lcRoundMoney(parseFloat(item.price ?? 0)),
      price: lcRoundMoney(parseFloat(item.discounted_price ?? item.price ?? 0)),
    })),
    value,
  );

  // Persist the bundle composition for checkout tracking.
  if (bundle) await lcStoreBundleInCart(bundle);
}

/** @type {any} */ (window).reboltCartCallBack = lcHandleReboltCartCallback;

// Undocumented Rebold event: fires on initial render and again on every selection change.
// Only the first "combo" firing counts as the view (other bundle types aren't covered yet).
let lcBundleViewEventSent = false;
let lcBundleWidgetAnnounced = false;

/** @param {any} event */
async function lcHandleBundleSelected(event) {
  // Tell the pixel this page has a Rebolt widget (only then it briefly holds back add_to_cart).
  if (!lcBundleWidgetAnnounced) {
    lcBundleWidgetAnnounced = true;
    window.Shopify?.analytics?.publish("custom_bundle_widget_present", {});
  }

  /** @type {any[]} */
  const items = event.detail?.bundleProduct ?? [];
  items.forEach((item) => lcComponentHandles.set(String(item.id), item.handle));
  const bundleDetail = event.detail?.bundleDetail;
  lcBundleHandle = bundleDetail?.created_product?.handle ?? lcBundleHandle;

  if (lcBundleViewEventSent || event.detail?.type !== "combo") return;
  // Rebold fires this once on mount with an empty array before its products are loaded;
  // wait for that to happen before treating it as the view.
  if (items.length === 0) return;
  lcBundleViewEventSent = true;

  // Rebold item prices are in cents. Each component gets its reduced price, and the
  // components always sum up to the bundle value.
  // Percentage rounding (set to whatever the cart actually charges):
  //   "total": discount on the bundle total, floored (= price shown in the Rebolt widget, NOT charged)
  //   "unit":  discount floored per unit (= what the cart actually charges)
  const LC_PERCENT_ROUNDING = "unit";

  const percent = Number(bundleDetail?.discount ?? 0);
  const listTotal = items.reduce(
    (sum, item) => sum + (item.price ?? 0) * (item.selected_qty ?? 1),
    0,
  );

  // Bundle discount in cents that is distributed across the components
  let totalDiscount = 0;
  if (bundleDetail?.discount_type === "0" && LC_PERCENT_ROUNDING === "total") {
    totalDiscount = Math.floor((listTotal * percent) / 100);
  } else if (bundleDetail?.discount_type === "1") {
    totalDiscount = Math.round(
      100 * (bundleDetail?.fixed_discount ?? 0) * Number(window.Shopify?.currency?.rate ?? 1),
    );
  }
  let discountLeft = totalDiscount;

  const components = items.map((item, index) => {
    const quantity = item.selected_qty ?? 1;
    const unitPrice = item.price ?? 0;
    let unitDiscount = 0;

    if (bundleDetail?.discount_type === "0" && LC_PERCENT_ROUNDING === "unit") {
      unitDiscount = Math.floor((unitPrice * percent) / 100);
    } else if (totalDiscount > 0 && listTotal > 0) {
      // Distributed proportionally to the list price, remainder on the last component.
      const lineDiscount =
        index === items.length - 1
          ? discountLeft
          : Math.round((totalDiscount * unitPrice * quantity) / listTotal);
      discountLeft -= lineDiscount;
      unitDiscount = lineDiscount / quantity;
    }

    return {
      handle: item.handle,
      productId: item.id,
      variantId: item.variantId,
      fallbackTitle: (item.options ?? []).filter(Boolean).join(" / "),
      quantity,
      uvpPrice: lcRoundMoney(unitPrice / 100),
      price: lcRoundMoney((unitPrice - unitDiscount) / 100),
    };
  });

  const value = lcRoundMoney(components.reduce((sum, c) => sum + c.price * c.quantity, 0));

  await lcPublishBundleEvent("custom_bundle_view_item", components, value);
}

window.addEventListener("rebolt:selectedBundle", lcHandleBundleSelected);